import React, { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Filter, X, Check, Heart, MessageCircle, Send, Bookmark } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";
import { portfolio } from "../data/mock";

// Helper component for fast & progressive image loading with blurred backdrop option for social/branding items
const PortfolioImage = ({ src, alt, category }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isSocialOrBranding = category === "Social Media" || category === "Branding";

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden bg-[#0e0d0b]">
      {/* Skeleton Shimmer */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-[#161513] via-[#22211f] to-[#161513] bg-[length:200%_100%] animate-shimmer flex items-center justify-center z-10">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--gold)]/20 border-t-[var(--gold)] animate-spin" />
        </div>
      )}

      {isSocialOrBranding ? (
        <div className="w-full h-full relative flex items-center justify-center p-2">
          {/* Blurred Backdrop */}
          <img
            src={src}
            alt=""
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover blur-2xl brightness-[0.35] transition-opacity duration-700 ${
              isLoaded ? "opacity-75" : "opacity-0"
            }`}
          />
          {/* Foreground Contain Image */}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            className={`relative z-10 max-w-full max-h-full object-contain transition-all duration-700 group-hover:scale-105 ${
              isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          />
        </div>
      ) : (
        /* Standard Cover Image for Websites, Films, etc. */
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0 ${
            isLoaded ? "opacity-60 group-hover:opacity-100" : "opacity-0 scale-105"
          }`}
        />
      )}
    </div>
  );
};

const categories = ["All", "Branding", "Websites", "Films", "Social Media", "Success Diaries"];

const PortfolioPage = () => {
  const [filter, setFilter] = useState("All");
  const [filteredItems, setFilteredItems] = useState(portfolio);
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    if (filter === "All") {
      setFilteredItems(portfolio);
    } else {
      setFilteredItems(portfolio.filter(item => item.category === filter));
    }
  }, [filter]);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="Portfolio — Cinematic & Digital Works"
        description="Explore the cinematic stories and digital engineering projects crafted by Meraki Solutions."
      />
      {/* <CustomCursor /> */}
      <div className="grain" />
      <Navbar />
      <main>
        <SectionStepper
          sections={[
            { id: "top", label: "Selected Chapters" },
            { id: "archive", label: "Archive" },
            { id: "storytelling", label: "Philosophy" }
          ]}
        />
        {/* Hero Section */}
        <section id="top" className="relative pt-44 pb-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full h-full object-cover opacity-45 grayscale brightness-90"
            >
              <source src="https://videos.pexels.com/video-files/7652719/7652719-hd_1080_1920_25fps.mp4" type="video/mp4" />
            </video>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-xs mb-6 block">The Archive</span>
              <h1 className="font-display text-6xl md:text-9xl mb-12 leading-none">
                Selected <br /> <span className="italic gold-text">Chapters</span>
              </h1>
            </motion.div>

            {/* Filters */}
            <div id="archive" className="flex flex-wrap items-center gap-4 mt-20 border-b border-[var(--rule)] pb-8">
              <div className="flex items-center gap-3 mr-6 text-[var(--gold)]">
                <Filter size={16} />
                <span className="font-impact text-[10px] tracking-widest uppercase">Filter by</span>
              </div>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-6 py-2 rounded-full text-[11px] uppercase tracking-widest transition-all duration-300 ${filter === cat
                    ? "bg-[var(--gold)] text-[var(--ink)]"
                    : "border border-[var(--rule)] text-[var(--cream-muted)] hover:border-[var(--gold)]"
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid Section */}
        <section className="pb-44">
          <div className="container-xl px-6">
            <motion.div
              layout
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    onClick={() => {
                      if (item.category === "Websites" && item.url) {
                        window.open(item.url, "_blank");
                      } else if (item.youtubeEmbedUrl) {
                        setLightboxVideo(item.youtubeEmbedUrl);
                      } else {
                        setLightboxImage(item.image);
                      }
                    }}
                    className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-[var(--ink-2)] border border-[var(--rule)] cursor-pointer"
                  >
                    {item.category === "Social Media" ? (
                      /* Premium Instagram Mockup Card */
                      <div className="w-full h-full flex flex-col justify-between bg-[#0e0d0b] border border-white/5 relative">
                        {/* 1. Header (Profile Mockup) */}
                        <div className="flex items-center justify-between p-4 border-b border-white/[0.05] bg-black/20 w-full z-20">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-impact bg-gradient-to-br from-[var(--gold)] to-[var(--copper)] text-black font-semibold">
                              {item.title.charAt(0)}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-display text-[12px] font-medium text-[var(--cream)] truncate max-w-[120px]">
                                {item.title.split(" ")[0]}
                              </span>
                              <span className="w-3 h-3 rounded-full bg-sky-500 flex items-center justify-center text-[6px] text-white">
                                <Check size={6} strokeWidth={4} />
                              </span>
                            </div>
                          </div>
                          <span className="font-impact text-[9px] tracking-wider text-[var(--gold)] uppercase bg-[var(--gold)]/10 px-2 py-0.5 rounded border border-[var(--gold)]/20">
                            POST
                          </span>
                        </div>

                        {/* 2. Visual Viewport (Center) */}
                        <div className="flex-1 w-full relative overflow-hidden bg-black/40">
                          <PortfolioImage src={item.image} alt={item.title} category={item.category} />
                        </div>

                        {/* 3. Instagram Interaction Bar */}
                        <div className="flex items-center justify-between px-4 py-2 border-t border-white/[0.02] bg-black/20 w-full z-20">
                          <div className="flex items-center gap-3 text-[var(--cream-muted)]">
                            <Heart size={14} className="hover:text-red-500 hover:fill-red-500 cursor-pointer transition-colors" />
                            <MessageCircle size={14} className="hover:text-[var(--gold)] cursor-pointer transition-colors" />
                            <Send size={14} className="hover:text-[var(--gold)] cursor-pointer transition-colors" />
                          </div>
                          <Bookmark size={14} className="text-[var(--cream-muted)] hover:text-[var(--gold)] cursor-pointer transition-colors" />
                        </div>

                        {/* 4. Footer Description */}
                        <div className="p-4 bg-black/40 border-t border-white/[0.03] z-20">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-display text-xl font-semibold text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors duration-300 truncate">
                                {item.title}
                              </h3>
                              <p className="text-[11px] text-[var(--cream-muted)] leading-relaxed font-body line-clamp-2 mt-0.5">
                                {item.desc}
                              </p>
                            </div>
                            {item.instagramUrl ? (
                              <a 
                                href={item.instagramUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="w-8 h-8 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 hover:bg-[var(--gold)] hover:text-black flex items-center justify-center transition-all shrink-0 self-end"
                                onClick={(e) => e.stopPropagation()}
                                title="View on Instagram"
                              >
                                <ArrowUpRight size={14} />
                              </a>
                            ) : (
                              <span className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 self-end">
                                <ArrowUpRight size={14} className="text-[var(--cream-muted)]" />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Standard Card Layout */
                      <>
                        <PortfolioImage src={item.image} alt={item.title} category={item.category} />
                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-transparent to-transparent opacity-80 pointer-events-none" />

                        <div className="absolute inset-0 p-8 flex flex-col justify-end transform transition-transform duration-500 pointer-events-none">
                          <span className="font-impact text-[10px] tracking-[0.3em] text-[var(--gold)] mb-3 block uppercase">
                            {item.category} • {item.year}
                          </span>
                          <h3 className="font-display text-3xl text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors duration-300">
                            {item.title}
                          </h3>
                          <div className="mt-6 flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            <div className="h-px w-12 bg-[var(--gold)]" />
                            <span className="text-[10px] tracking-[0.2em] text-[var(--cream)] font-mono lowercase">
                              {item.category === "Websites" ? (item.url ? item.url.replace("https://www.", "").replace("https://", "").replace(/\/$/, "") : "launch site") : item.youtubeEmbedUrl ? "Watch Video" : "View Detail"}
                            </span>
                            <ArrowUpRight size={14} className="text-[var(--gold)]" />
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>
        {/* Process Preview Section */}
        <section id="storytelling" className="py-44 bg-[var(--ink-2)] border-y border-[var(--rule)] relative overflow-hidden">
          <div className="container-xl px-6 relative z-10">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div>
                <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-xs mb-6 block">Beyond the Result</span>
                <h2 className="font-display text-5xl md:text-8xl mb-8 leading-[1.1]">The Art of <br /><span className="italic gold-text">Storytelling</span></h2>
                <p className="text-[var(--cream-muted)] text-lg mb-12 leading-relaxed">
                  Every project is a journey. We don't just deliver a file; we engineer an experience. Our process combines deep research with cinematic instinct to create work that resonates on a visceral level.
                </p>
                <Link to="/about" className="btn-ghost">Learn our Process <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden grayscale opacity-40 border border-[var(--rule)]">
                    <img src="https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover" alt="Process 1" />
                  </div>
                  <div className="aspect-square rounded-3xl overflow-hidden grayscale opacity-40 border border-[var(--rule)]">
                    <img src="https://images.unsplash.com/photo-1493612276216-ee3925520721?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover" alt="Process 2" />
                  </div>
                </div>
                <div className="space-y-4 pt-12">
                  <div className="aspect-square rounded-3xl overflow-hidden grayscale opacity-40 border border-[var(--rule)]">
                    <img src="https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover" alt="Process 3" />
                  </div>
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden grayscale opacity-40 border border-[var(--rule)]">
                    <img src="https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover" alt="Process 4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 1. PREMIUM VIDEO LIGHTBOX MODAL */}
        <AnimatePresence>
          {lightboxVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
              onClick={() => setLightboxVideo(null)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 30 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 30 }}
                transition={{ type: "spring", damping: 25, stiffness: 180 }}
                className="relative w-full max-w-4xl aspect-video rounded-3xl overflow-hidden border border-[rgba(201,169,97,0.25)] shadow-[0_30px_60px_rgba(0,0,0,0.85)] bg-black"
                onClick={(e) => e.stopPropagation()}
              >
                <iframe
                  className="w-full h-full border-0 absolute inset-0"
                  src={lightboxVideo}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />

                <button
                  onClick={() => setLightboxVideo(null)}
                  className="absolute top-4 right-4 z-[1000] p-2.5 bg-black/85 hover:bg-[var(--gold)] hover:text-black border border-white/10 rounded-full text-white transition-all shadow-lg backdrop-blur-md flex items-center justify-center focus:outline-none"
                >
                  <span className="text-sm font-bold">✕</span>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 2. PREMIUM IMAGE LIGHTBOX MODAL */}
        <AnimatePresence>
          {lightboxImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
              onClick={() => setLightboxImage(null)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 30 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 30 }}
                transition={{ type: "spring", damping: 25, stiffness: 180 }}
                className="relative w-full max-w-[500px] aspect-[4/5] rounded-3xl overflow-hidden border border-[rgba(201,169,97,0.25)] shadow-[0_30px_60px_rgba(0,0,0,0.85)] bg-black flex items-center justify-center p-2"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={lightboxImage}
                  alt="Portfolio Work Detail"
                  className="w-full h-full object-cover rounded-[20px]"
                />

                <button
                  onClick={() => setLightboxImage(null)}
                  className="absolute top-4 right-4 z-[1000] p-2.5 bg-black/85 hover:bg-[var(--gold)] hover:text-black border border-white/10 rounded-full text-white transition-all shadow-lg backdrop-blur-md flex items-center justify-center focus:outline-none"
                >
                  <span className="text-sm font-bold">✕</span>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </main>
      <Footer />
    </div>
  );
};

export default PortfolioPage;
